import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/**
 * SINGLE entry point for every AI call in the app.
 *
 * Talks to the `ai-features` edge function, which routes the request to
 * Google Gemini (model pinned in supabase/functions/_shared/gemini.ts).
 * The legacy `useClaudeAI` / `useOpenAIService` hooks are thin wrappers over
 * this one so older call sites keep working without duplicate logic.
 */
export interface AICallParams {
  feature: string;
  /** The question / instruction for the model. */
  input?: string;
  /** The data the model may reason over. Never fabricated server-side. */
  dataset?: unknown;
  options?: Record<string, unknown>;
  industry?: string;
  interactionType?: string;
}

export interface AIResult<T = any> {
  success: boolean;
  response?: string;
  json?: T;
  error?: string;
}

export async function callAI<T = any>(params: AICallParams): Promise<AIResult<T>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    return { success: false, error: "Please sign in to use AI features." };
  }

  const { data, error } = await supabase.functions.invoke("ai-features", {
    body: params,
  });

  if (error) {
    return { success: false, error: error.message || "AI service unreachable." };
  }
  if (!data?.success) {
    return {
      success: false,
      error: data?.error || "The AI returned no result.",
      response: data?.response,
    };
  }
  return data as AIResult<T>;
}

export const useAI = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastResult, setLastResult] = useState<AIResult | null>(null);

  const call = async <T = any>(
    params: AICallParams,
    opts: { silent?: boolean } = {},
  ): Promise<AIResult<T>> => {
    setIsLoading(true);
    setLastResult(null);
    try {
      const result = await callAI<T>(params);
      setLastResult(result);
      if (!result.success && !opts.silent) toast.error(result.error!);
      return result;
    } catch (e: any) {
      const result = { success: false, error: e?.message || "Unexpected AI error" };
      setLastResult(result);
      if (!opts.silent) toast.error(result.error);
      return result as AIResult<T>;
    } finally {
      setIsLoading(false);
    }
  };

  return { call, isLoading, lastResult };
};
